import { useState } from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { useChainData } from 'modules/common';
import { useTranslation } from 'modules/i18n';
import {
  useGetAccountAddressQuery,
  useGetAccountBalanceQuery,
} from 'modules/store';

import { DepositModal } from '../DepositModal';
import translation from './paymentForm.translation';

interface Props {
  onSubmit: () => void;
}

export function PaymentForm({ onSubmit }: Props): JSX.Element {
  const { classes, theme } = useStyles();
  const { t, keys } = useTranslation(translation);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpenModal, setIsOpenModal] = useState(false);
  const chainData = useChainData();
  const { data: accountAddress = '' } = useGetAccountAddressQuery();
  const { data: accountBalance } = useGetAccountBalanceQuery({
    accountAddress,
  });

  return (
    <form onSubmit={onSubmit} className={classes.container}>
      <Typography variant="button2">{t(keys.title)}</Typography>

      <div className={classes.list}>
        <div className={classes.listItem}>
          <Typography variant="subtitle2">{t(keys.accountFunds)}:</Typography>

          <Box display="flex" alignItems="center">
            <Typography variant="subtitle2" textAlign="right">
              {t(keys.depositValue, {
                balance: isMobile
                  ? accountBalance?.decimalPlaces(2).toString()
                  : accountBalance?.toString(),
                unit: chainData?.tokenName,
              })}
            </Typography>

            <Button
              data-tid="create-task-payment-deposit-button"
              className={classes.depositRowButton}
              variant="outlined"
              onClick={() => setIsOpenModal(true)}
            >
              {t(keys.deposit)}
            </Button>
          </Box>
        </div>
      </div>

      <div className={classes.depositPanel}>
        <Typography variant="body4" color={theme => theme.palette.grey[500]}>
          {t(keys.description)}
        </Typography>

        <Button variant="outlined" onClick={() => setIsOpenModal(true)}>
          {t(keys.deposit)}
        </Button>
      </div>

      <Button
        data-tid="create-task-payment-submit-button"
        size="large"
        type="submit"
        sx={{ mt: 10 }}
      >
        {t(keys.submit)}
      </Button>

      <DepositModal onClose={() => setIsOpenModal(false)} open={isOpenModal} />
    </form>
  );
}

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(8),
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    '&:not(:first-child)': {
      marginTop: theme.spacing(8),
    },
  },
  time: {
    '&&': {
      color: theme.palette.grey[600],
    },
  },
  depositRowButton: {
    '&&': {
      marginLeft: theme.spacing(4),

      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
  },

  depositPanel: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    gap: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}));
